/**
 * Tracks the user id this tab's React state believes it is acting as.
 *
 * Read synchronously from the SDK request interceptor so every outgoing
 * authenticated request can carry an `X-Acting-User-Id` header. The backend
 * compares against the cookie-resolved user and returns 412 on mismatch.
 *
 * Why a module-level variable instead of React state:
 * - The SDK client is initialized outside the React tree, so its
 *   interceptors can't subscribe to React hooks.
 * - The value must be readable from any synchronous call site, not just
 *   during a render.
 *
 * Source of truth: written by AuthProvider whenever the `/me` query
 * settles. Cleared on logout / before login flips identities so a stale
 * id never trails behind the cookie.
 *
 * Encapsulation: the writer (`__setActingUserIdInternal`) carries a
 * leading double-underscore and is restricted by an ESLint
 * `no-restricted-imports` rule to AuthProvider only. Other modules may
 * only call `getActingUserId()`. This prevents a future contributor from
 * corrupting the global from an unrelated component, which would cause
 * every authenticated request in the tab to 412 until reload.
 *
 * Background: SUP-3373.
 */

let actingUserId: string | undefined

/**
 * Internal writer for the acting-user id. Restricted by ESLint
 * `no-restricted-imports` — only AuthProvider may import this. The
 * leading `__` is the signal that this is not part of the public surface.
 */
export function __setActingUserIdInternal(id?: string): void {
  actingUserId = id
}

export function getActingUserId(): string | undefined {
  return actingUserId
}

/**
 * Public clear-only helper for code paths that rotate the cookie outside
 * AuthProvider (e.g. superuser impersonation start). Safe to expose
 * because it can only clear the id — it cannot set an arbitrary value
 * that could be used to forge identity. Callers should invoke this
 * BEFORE the request that follows their cookie rotation, then let
 * AuthProvider's useEffect re-sync the id from the refetched `/me`.
 */
export function clearActingUserId(): void {
  actingUserId = undefined
}
